import { fetchAuthSession, signOut } from "aws-amplify/auth";
import * as React from "react";

import { Env } from "@/shared/environment";
import { HttpClient, robotoHeaders } from "@/shared/http";

import { HttpClientContext } from "./HttpClientContext";

export function HttpClientProvider({ children }: React.PropsWithChildren) {
  const [resourceOwnerId, setResourceOwnerId] = React.useState<
    string | undefined
  >(undefined);

  const httpClientContext = React.useMemo(() => {
    return {
      httpClient: new HttpClient({
        // The active org is sent as both org headers, because routes are split
        // between two auth rules that read different ones: REQUIRES_OWNER_ORG
        // reads X-Roboto-Resource-Owner-Id, REQUIRES_CALLER_ORG reads
        // X-Roboto-Org-Id. Either header alone leaves the other rule's routes
        // failing with RobotoNoOrgProvidedException for anyone in more than one
        // org — a single-org caller never noticed, because the service falls
        // back to their only org. Callers that need a *different* org for one
        // request still pass `robotoHeaders({ orgId })` themselves; per-request
        // headers merge over these (see HttpClient's #mergeWithBaseHeaders).
        baseHeaders: robotoHeaders({
          orgId: resourceOwnerId,
          resourceOwnerId,
        }),
        baseUrl: new URL(Env.robotoServiceBaseUrl),
        bearerTokenVendor: async () => {
          const session = await fetchAuthSession();
          return session.tokens?.idToken?.toString();
        },
        onAuthFailure: signOut,
      }),
      setResourceOwnerId,
    };
  }, [resourceOwnerId]);

  return (
    <HttpClientContext.Provider value={httpClientContext}>
      {children}
    </HttpClientContext.Provider>
  );
}
